import React, { useState } from "react";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Popover,
  Typography,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ChromePicker } from "react-color";
import { toast } from "react-toastify";
import {
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  BookmarkBorder as BookmarkBorderIcon,
} from "@mui/icons-material";
import * as messages from "../../../utils/constants";
import Palette from "./Palette";
import dayjs from "dayjs";
import { MRT_EditActionButtons as ActioButtons } from "material-react-table";
import styled from "styled-components";
import ItemSelectChip from "./ItemSelectChip";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CustomEditActionButtons from "./EditActionButtons";

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-input {
    padding: 0;
    height: 0;
    visibility: hidden;
  }
`;
const convertAlignmentOption = (value) => {
  if (value === "start") return "start";
  if (value === "end") return "end";
  return value || "start";
};
function EditRowDialog({
  table,
  row,
  itemData,
  initialData,
  handleUpdateOrder,
}) {
  const [newRow, setNewRow] = useState({
    order_name: initialData.order_name || "",
    item_id: initialData.item_id || "",
    qty: initialData.qty || "",
    pce: initialData.pce || "",
    lot_size: initialData.lot_size || "",
    priority: initialData.order_priority || 1,
    start_date: initialData.start_date ? dayjs(initialData.start_date) : null,
    due_date: initialData.due_date ? dayjs(initialData.due_date) : null,
    color: initialData.color || "#000000",
    alignment_option: convertAlignmentOption(initialData.alignment_option),
    confirm_status:
      initialData.confirm_status !== undefined ? initialData.confirm_status : 1,
  });
  const [activeColor, setActiveColor] = useState(initialData.color);
  const [anchorEl, setAnchorEl] = useState(null);
  const onTileClick = (color) => {
    handleInputChange("color", color);
    toast.success(messages.successColorSelect);
    setActiveColor(color);
  };
  const setEditingRow = (field, value) => {
    table.setEditingRow({
      ...row,
      [field]: value,
    });
  };
  const handleInputChange = (field, value) => {
    setNewRow((prev) => ({ ...prev, [field]: value }));
    setEditingRow(field, value);
    if (field === "color") {
      setActiveColor(value);
    }
  };

  const handleAlignmentChange = (event, newAlignment) => {
    if (newAlignment !== null) {
      handleInputChange("alignment_option", newAlignment);
    }
  };

  const handleConfirmStatusChange = (event, newConfirmStatus) => {
    if (newConfirmStatus !== null) {
      handleInputChange("confirm_status", newConfirmStatus);
    }
  };

  const handlePceChange = (e) => {
    const value = e.target.value;
    if (
      value === "" ||
      (/^[1-9]\d*$/.test(value) &&
        parseInt(value) >= 1 &&
        parseInt(value) <= 100)
    ) {
      handleInputChange("pce", value);
    }
  };
  const handleLotSizeChange = (e) => {
    const value = e.target.value;
    if (value === "" || (/^[1-9]\d*$/.test(value) && parseInt(value) >= 1)) {
      handleInputChange("lot_size", value);
    }
  };
  const handleOpenChromePicker = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseChromePicker = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  return (
    <>
      <DialogTitle
        variant="h5"
        sx={{ fontSize: "18px !important", color: "#111" }}
      >
        생산계획 수정
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
      >
        <TextField
          label="주문명"
          value={newRow.order_name}
          onChange={(e) => handleInputChange("order_name", e.target.value)}
          fullWidth
        />
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <ItemSelectChip
            selectedItem={
              newRow.item_id
                ? [
                    itemData.filter((c) => c.item_id === newRow.item_id)[0]
                      .item_name,
                  ]
                : []
            }
            setSelectedItem={(items) => handleInputChange("item_id", items[0])}
            itemList={itemData}
            disabledProps={true}
          />
          <TextField
            label="수량"
            type="number"
            value={newRow.qty}
            onChange={(e) => handleInputChange("qty", e.target.value)}
            fullWidth
          />
        </div>
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <TextField
            label="PCE"
            value={newRow.pce}
            onChange={handlePceChange}
            fullWidth
            type="number"
            inputProps={{
              step: 1,
              min: 1,
              max: 100,
            }}
            helperText="1 이상 100 이하의 정수"
          />
          <TextField
            label="LOT SIZE"
            value={newRow.lot_size}
            onChange={handleLotSizeChange}
            fullWidth
            type="number"
            inputProps={{
              step: 1,
              min: 1,
            }}
            helperText="1 이상의 정수"
          />
        </div>

        {/* 확정/예약 상태 토글 추가 */}
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
            주문 상태
          </Typography>
          <ToggleButtonGroup
            value={newRow.confirm_status}
            exclusive
            onChange={handleConfirmStatusChange}
            aria-label="confirm status"
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1.5,
                borderRadius: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              },
            }}
          >
            <ToggleButton value={1} aria-label="confirmed">
              <CheckCircleIcon sx={{ mr: 1, fontSize: 18 }} />
              확정
            </ToggleButton>
            <ToggleButton value={0} aria-label="reserved">
              <BookmarkBorderIcon sx={{ mr: 1, fontSize: 18 }} />
              예약
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {newRow.confirm_status === 1
              ? "확정된 주문으로 등록됩니다"
              : "예약 상태로 등록됩니다"}
          </Typography>
        </Box>

        <StyledTextField
          label="우선순위"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <Box sx={{ width: "1900%", px: 1, py: 2 }}>
                <Slider
                  value={newRow.priority}
                  onChange={(_, value) => handleInputChange("priority", value)}
                  min={1}
                  max={100}
                  step={null}
                  marks={[
                    { value: 1, label: "" },
                    { value: 10, label: "" },
                    { value: 20, label: "" },
                    { value: 30, label: "" },
                    { value: 40, label: "" },
                    { value: 50, label: "" },
                    { value: 60, label: "" },
                    { value: 70, label: "" },
                    { value: 80, label: "" },
                    { value: 90, label: "" },
                    { value: 100, label: "" },
                  ]}
                  valueLabelDisplay="off"
                  sx={{
                    "& .MuiSlider-rail": {
                      opacity: 0.3,
                    },
                    "& .MuiSlider-thumb": {
                      width: 16,
                      height: 16,
                    },
                    "& .MuiSlider-mark": {
                      backgroundColor: "currentColor",
                      height: 8,
                      width: 2,
                    },
                    "& .MuiSlider-markActive": {
                      backgroundColor: "currentColor",
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 1,
                    mt: 1,
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    "& .selected": {
                      fontWeight: 700,
                      color: "text.primary",
                    },
                  }}
                >
                  {[1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((num) => (
                    <span
                      key={num}
                      className={num === newRow.priority ? "selected" : ""}
                    >
                      {num}
                    </span>
                  ))}
                </Box>
              </Box>
            ),
          }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <div style={{ display: "flex", gap: "0.3rem" }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="시작 시간"
              value={newRow.start_date}
              onChange={(date) => handleInputChange("start_date", date)}
              format="YYYY년 MM월 DD일 HH시 mm분"
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="납기일"
              value={newRow.due_date}
              onChange={(date) => handleInputChange("due_date", date)}
              format="YYYY년 MM월 DD일 HH시 mm분"
              sx={{
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "0.6rem",
                },
              }}
            />
          </LocalizationProvider>
        </div>
        {/* 정렬 옵션 추가 */}
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
            스케줄링 정렬 옵션
          </Typography>
          <ToggleButtonGroup
            value={newRow.alignment_option}
            exclusive
            onChange={handleAlignmentChange}
            aria-label="alignment option"
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 1.5,
                borderRadius: 1,
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
                "&.Mui-selected": {
                  backgroundColor: "primary.main",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "primary.dark",
                  },
                },
              },
            }}
          >
            <ToggleButton value="start" aria-label="start alignment">
              <ScheduleIcon sx={{ mr: 1, fontSize: 18 }} />
              시작일 기준
            </ToggleButton>
            <ToggleButton value="end" aria-label="end alignment">
              <FlagIcon sx={{ mr: 1, fontSize: 18 }} />
              납기일 기준
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: "block" }}
          >
            {newRow.alignment_option === "start"
              ? "시작일을 기준으로 스케줄링됩니다"
              : "납기일을 기준으로 역산하여 스케줄링됩니다"}
          </Typography>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            색상 선택
          </Typography>
          <Palette
            handleOpenChromePicker={handleOpenChromePicker}
            title="더 많은 색상 선택"
            handleInputChange={handleInputChange}
            colors1={[
              { value: "#96bef0" },
              { value: "#ffb5ea" },
              { value: "#8affad" },
              { value: "#9ccdff" },
              { value: "#ea9cfb", lightLabel: true },
              { value: "#f0be68", lightLabel: true },
              { value: "#64f0f0", lightLabel: true },
              { value: "#efb9b9", lightLabel: true },
              { value: "#6ff080", lightLabel: true },
              { value: "#b7e1f0", lightLabel: true },
            ]}
            colors2={[
              { value: "#ffd179" },
              { value: "#c5ff7b" },
              { value: "#64b9ff" },
              { value: "#d87570" },
              { value: "#aff3b2", lightLabel: true },
              { value: "#a2a6d8", lightLabel: true },
              { value: "#ffbe96", lightLabel: true },
              { value: "#c882e2", lightLabel: true },
              { value: "#c4a9d3", lightLabel: true },
              { value: "#aad2e8", lightLabel: true },
            ]}
            activeColor={activeColor}
            setActiveColor={setActiveColor}
            onTileClick={onTileClick}
          />
        </Box>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseChromePicker}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
        >
          <Box sx={{ p: 2, backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
            <ChromePicker
              color={newRow.color}
              onChangeComplete={(color) => {
                handleInputChange("color", color.hex);
                handleCloseChromePicker();
              }}
            />
          </Box>
        </Popover>
      </DialogContent>
      <DialogActions
        sx={{
          paddingTop: "1.5rem !important",
        }}
      >
        <CustomEditActionButtons
          table={table}
          row={row}
          handleUpdateOrder={handleUpdateOrder}
        />
      </DialogActions>
    </>
  );
}

export default EditRowDialog;
